import { Theme } from '@core/enums';
import { Device, until } from '@helpers/media';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import Button from '@stories/Elements/Forms/Buttons/Button';
import LazyImage from '@stories/Elements/Media/LazyImage/LazyImage';
import React, { FunctionComponent } from 'react';
import { CategoryCardStyles } from './CategoryCard.styles';

export interface CategoryCardModel {
  /**
   * Cards have the ability to set an individual theme colour
   */
  theme: Theme;
  categoryId: string;
  imageUrl: string;
  imageAlt: string;
  text: string;
  url: string;
  buttonText?: string;
}

export interface CategoryCardProps {
  theme: Theme;
  category: CategoryCardModel;
  index?: number;
  /**
   * Optional image sizes can be passed in and set accordingly
   */
  imageSizes?: string;
  /**
   * Used to set the delayInterval for animation based of how many items in a given row
   */
  rowItems?: number;
  /**
   * Styled to center content when set to true
   */
  isCentered?: boolean;
  /**
   * If true, the card will not display any images or buttons, only the text content.
   */
  textOnly?: boolean;
}

const CategoryCard: FunctionComponent<CategoryCardProps> = ({
  theme,
  category,
  index = 0,
  rowItems = useMedia([until(Device.Tablet), until(Device.TabletLarge), until(Device.DesktopSmall)], [1, 2, 3], 4),
  isCentered = false,
  textOnly = false,
}) => {
  const [elementRef, inView] = useInView<HTMLAnchorElement>({ threshold: 0.15 });

  const baseDelay = 0;
  const indexDelay = index % rowItems;
  const delayInterval = baseDelay + indexDelay * 100;

  // set button theme to red if card theme is black, otherwise display black button
  const buttonTheme = theme === Theme.Black ? 'red' : 'black-grey';

  return (
    <CategoryCardStyles.Card
      href={category.url}
      ref={elementRef}
      inView={inView}
      delay={delayInterval}
      theme={theme ?? category.theme}
      isCentered={isCentered}
      data-text-only={textOnly}
    >
      {!textOnly && (
        <CategoryCardStyles.ImageWrapper>
          <LazyImage disableBlur={true} width={385} height={200} src={category.imageUrl} alt={category.imageAlt} />
        </CategoryCardStyles.ImageWrapper>
      )}
      <CategoryCardStyles.Text>
        <h3>{category.text}</h3>
        {!textOnly && category.buttonText && (
          <CategoryCardStyles.ButtonWrapper>
            <Button theme={buttonTheme} text={category.buttonText} fakeButton={true} icon={'arrow'} />
          </CategoryCardStyles.ButtonWrapper>
        )}
      </CategoryCardStyles.Text>
    </CategoryCardStyles.Card>
  );
};

export default CategoryCard;
